import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import ConfirmModal from '../components/ConfirmModal.jsx';
import './Events.css';

const WINDOWS_TO_IANA_TIMEZONE = {
  'Eastern Standard Time': 'America/New_York',
  'Central Standard Time': 'America/Chicago',
  'Mountain Standard Time': 'America/Denver',
  'Pacific Standard Time': 'America/Los_Angeles',
  UTC: 'UTC'
};

const getIanaTimeZone = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  return WINDOWS_TO_IANA_TIMEZONE[normalized] || normalized;
};

const getDatePartsInTimeZone = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = Number(part.value);
    return acc;
  }, {});

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second
  };
};

const getUtcDateForWallClockInTimeZone = (components, timeZone) => {
  let utcMillis = Date.UTC(
    components.year,
    components.month - 1,
    components.day,
    components.hour,
    components.minute,
    components.second || 0
  );

  for (let i = 0; i < 2; i += 1) {
    const zonedParts = getDatePartsInTimeZone(new Date(utcMillis), timeZone);
    const zonedMillis = Date.UTC(
      zonedParts.year,
      zonedParts.month - 1,
      zonedParts.day,
      zonedParts.hour,
      zonedParts.minute,
      zonedParts.second || 0
    );
    const targetMillis = Date.UTC(
      components.year,
      components.month - 1,
      components.day,
      components.hour,
      components.minute,
      components.second || 0
    );
    utcMillis += (targetMillis - zonedMillis);
  }

  return new Date(utcMillis);
};

const parseEventDateTime = (value, eventTimeZone) => {
  if (!value) return null;

  const ianaTimeZone = getIanaTimeZone(eventTimeZone);

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;

    const wallClock = {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes(),
      second: value.getUTCSeconds()
    };

    return ianaTimeZone
      ? getUtcDateForWallClockInTimeZone(wallClock, ianaTimeZone)
      : new Date(
        wallClock.year,
        wallClock.month - 1,
        wallClock.day,
        wallClock.hour,
        wallClock.minute,
        wallClock.second,
        value.getUTCMilliseconds()
      );
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      const wallClock = {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
        second: Number(second || 0)
      };

      return ianaTimeZone
        ? getUtcDateForWallClockInTimeZone(wallClock, ianaTimeZone)
        : new Date(
          wallClock.year,
          wallClock.month - 1,
          wallClock.day,
          wallClock.hour,
          wallClock.minute,
          wallClock.second
        );
    }
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatDateTimeLocalValue = (value, eventTimeZone) => {
  if (!value) return '';
  const date = parseEventDateTime(value, eventTimeZone);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizeDateTimeForApi = (value) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00`;
  }

  return trimmed;
};

const Events = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasAnyPermission } = useAuth();
  const canViewAllEvents = hasAnyPermission(['events:read:any']);
  const [events, setEvents] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCleanup, setDeleteCleanup] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [createdTemplate, setCreatedTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionType: 'text',
    isRequired: false,
    options: '',
    sortOrder: 0
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventContactSuggestions, setEventContactSuggestions] = useState([]);
  const [showEventContactSuggestions, setShowEventContactSuggestions] = useState(false);
  const [searchingEventContacts, setSearchingEventContacts] = useState(false);
  const [eventContactSearchError, setEventContactSearchError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    eventEndDate: '',
    eventTimeZone: 'Eastern Standard Time',
    location: '',
    virtualLink: '',
    eventContact: '',
    maxAttendees: '',
    isPublic: true,
    requireFirstName: false,
    requireLastName: false,
    requireCompany: false,
    requirePhone: false,
    requireNotes: false
  });

  const currentUserEmail = (user?.mail || user?.email || '').trim();
  const eventContactSearchDebounceRef = useRef(null);
  const eventContactSearchRequestRef = useRef(0);
  const eventContactSearchContainerRef = useRef(null);

  useEffect(() => {
    loadEvents();
  }, [showAllEvents, canViewAllEvents]);

  useEffect(() => {
    const editEventId = location.state?.editEventId;
    if (!editEventId || events.length === 0) return;

    const targetEvent = events.find(item => String(item.EventId) === String(editEventId));
    if (!targetEvent) return;

    handleEdit(targetEvent);
    navigate(location.pathname, { replace: true, state: {} });
  }, [events, location.pathname, location.state, navigate]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!eventContactSearchContainerRef.current?.contains(event.target)) {
        setShowEventContactSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    return () => {
      if (eventContactSearchDebounceRef.current) {
        clearTimeout(eventContactSearchDebounceRef.current);
      }
    };
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = canViewAllEvents && showAllEvents
        ? await api.get('/admin/events')
        : await api.get('/events');
      setEvents(response.data);
    } catch (err) {
      setError('Failed to load events');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const nextValue = type === 'checkbox' ? checked : value;

      if (name === 'eventDate' && !editingEvent) {
        const shouldSyncEndDate = !prev.eventEndDate || prev.eventEndDate === prev.eventDate;

        return {
          ...prev,
          eventDate: nextValue,
          eventEndDate: shouldSyncEndDate ? nextValue : prev.eventEndDate
        };
      }

      return {
        ...prev,
        [name]: nextValue
      };
    });
  };

  const getActiveEventContactTerm = (value) => {
    const source = String(value || '');
    const chunks = source.split(',');
    return (chunks[chunks.length - 1] || '').trim();
  };

  const handleEventContactChange = (value) => {
    setFormData(prev => ({
      ...prev,
      eventContact: value
    }));

    const activeTerm = getActiveEventContactTerm(value);
    if (eventContactSearchDebounceRef.current) {
      clearTimeout(eventContactSearchDebounceRef.current);
    }

    if (activeTerm.length < 2) {
      setEventContactSuggestions([]);
      setSearchingEventContacts(false);
      setShowEventContactSuggestions(false);
      setEventContactSearchError('');
      return;
    }

    eventContactSearchDebounceRef.current = setTimeout(async () => {
      const requestId = eventContactSearchRequestRef.current + 1;
      eventContactSearchRequestRef.current = requestId;

      try {
        setSearchingEventContacts(true);
        setEventContactSearchError('');
        const response = await api.get('/events/entra-users/search', {
          params: { q: activeTerm }
        });

        if (eventContactSearchRequestRef.current !== requestId) {
          return;
        }

        const items = Array.isArray(response.data) ? response.data : [];
        setEventContactSuggestions(items);
        setShowEventContactSuggestions(true);
      } catch (err) {
        if (eventContactSearchRequestRef.current !== requestId) {
          return;
        }
        setEventContactSuggestions([]);
        setShowEventContactSuggestions(false);
        setEventContactSearchError(err.response?.data?.error || 'Unable to search Entra users right now.');
      } finally {
        if (eventContactSearchRequestRef.current === requestId) {
          setSearchingEventContacts(false);
        }
      }
    }, 250);
  };

  const handleSelectEventContactSuggestion = (selectedUser) => {
    const selectedEmail = String(selectedUser?.email || '').trim();
    if (!selectedEmail) return;

    const chunks = String(formData.eventContact || '').split(',');
    chunks[chunks.length - 1] = selectedEmail;

    const unique = [];
    const seen = new Set();
    chunks
      .map(chunk => chunk.trim())
      .filter(Boolean)
      .forEach((email) => {
        const normalized = email.toLowerCase();
        if (seen.has(normalized)) return;
        seen.add(normalized);
        unique.push(email);
      });

    setFormData(prev => ({
      ...prev,
      eventContact: unique.length > 0 ? `${unique.join(', ')}, ` : ''
    }));
    setEventContactSuggestions([]);
    setShowEventContactSuggestions(false);
    setSearchingEventContacts(false);
    setEventContactSearchError('');
  };

  const handleQuestionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQuestionForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addQuestion = () => {
    if (!questionForm.questionText.trim()) return;
    setQuestions(prev => ([
      ...prev,
      {
        questionText: questionForm.questionText.trim(),
        questionType: questionForm.questionType,
        isRequired: questionForm.isRequired,
        options: questionForm.options,
        sortOrder: Number(questionForm.sortOrder) || prev.length
      }
    ]));
    setQuestionForm({
      questionText: '',
      questionType: 'text',
      isRequired: false,
      options: '',
      sortOrder: 0
    });
  };

  const removeQuestion = (index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Preserve local wall-clock value from datetime-local to avoid timezone drift
      const eventData = {
        ...formData,
        eventDate: normalizeDateTimeForApi(formData.eventDate),
        eventEndDate: normalizeDateTimeForApi(formData.eventEndDate),
        eventTimeZone: formData.eventTimeZone || 'Eastern Standard Time',
        templateId: editingEvent?.TemplateId || null
      };
      
      if (editingEvent) {
        await api.put(`/events/${editingEvent.EventId}`, eventData);
      } else {
        const response = await api.post('/events', eventData);
        const eventId = response.data.EventId;

        if (questions.length > 0) {
          for (const question of questions) {
            const options = question.options
              ? question.options.split(',').map(opt => opt.trim()).filter(Boolean)
              : [];

            await api.post(`/events/${eventId}/questions`, {
              questionText: question.questionText,
              questionType: question.questionType,
              isRequired: question.isRequired,
              sortOrder: question.sortOrder,
              options
            });
          }
        }
      }
      setShowModal(false);
      resetForm();
      loadEvents();
    } catch (err) {
      alert('Failed to save event: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setQuestions([]);
    setFormData({
      title: event.Title,
      description: event.Description || '',
      eventDate: formatDateTimeLocalValue(event.EventDate, event.EventTimeZone),
      eventEndDate: formatDateTimeLocalValue(event.EventEndDate, event.EventTimeZone),
      eventTimeZone: event.EventTimeZone || 'Eastern Standard Time',
      location: event.Location || '',
      virtualLink: event.VirtualLink || '',
      eventContact: event.EventContact || '',
      maxAttendees: event.MaxAttendees || '',
      isPublic: event.IsPublic,
      requireFirstName: Boolean(event.RequireFirstName),
      requireLastName: Boolean(event.RequireLastName),
      requireCompany: Boolean(event.RequireCompany),
      requirePhone: Boolean(event.RequirePhone),
      requireNotes: Boolean(event.RequireNotes)
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/events/${deleteTarget.EventId}`, {
        params: { cleanup: deleteCleanup ? 'true' : 'false' }
      });
      setShowDeleteModal(false);
      setDeleteTarget(null);
      setDeleteCleanup(false);
      loadEvents();
    } catch (err) {
      alert('Failed to delete event: ' + (err.response?.data?.error || err.message));
    }
  };

  const openDeleteModal = (event) => {
    setDeleteTarget(event);
    setDeleteCleanup(false);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeleteCleanup(false);
  };

  const handleGenerateTemplate = async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/generate-template`);
      setCreatedTemplate(response.data);
      setShowTemplateModal(true);
      loadEvents();
    } catch (err) {
      alert('Failed to generate template: ' + (err.response?.data?.error || err.message));
    }
  };

  const resetForm = (prefillWithCurrentUserEmail = true) => {
    setFormData({
      title: '',
      description: '',
      eventDate: '',
      eventEndDate: '',
      eventTimeZone: 'Eastern Standard Time',
      location: '',
      virtualLink: '',
      eventContact: prefillWithCurrentUserEmail ? currentUserEmail : '',
      maxAttendees: '',
      isPublic: true,
      requireFirstName: false,
      requireLastName: false,
      requireCompany: false,
      requirePhone: false,
      requireNotes: false
    });
    setEditingEvent(null);
    setQuestions([]);
    setQuestionForm({
      questionText: '',
      questionType: 'text',
      isRequired: false,
      options: '',
      sortOrder: 0
    });
    setEventContactSuggestions([]);
    setShowEventContactSuggestions(false);
    setSearchingEventContacts(false);
    setEventContactSearchError('');
  };

  const formatDate = (dateString, eventTimeZone) => {
    const parsed = parseEventDateTime(dateString, eventTimeZone);
    if (!parsed) return '';

    const ianaTimeZone = getIanaTimeZone(eventTimeZone);

    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: ianaTimeZone || undefined,
        timeZoneName: ianaTimeZone ? 'short' : undefined
      }).format(parsed);
    } catch {
      return parsed.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getEventStatus = (event) => {
    const now = new Date();
    const eventDate = parseEventDateTime(event.EventDate, event.EventTimeZone);
    if (!eventDate) return { label: 'Upcoming', class: 'upcoming' };
    const endDate = event.EventEndDate
      ? (parseEventDateTime(event.EventEndDate, event.EventTimeZone) || eventDate)
      : eventDate;
    
    if (endDate < now) return { label: 'Past', class: 'past' };
    if (eventDate > now) return { label: 'Upcoming', class: 'upcoming' };
    return { label: 'Ongoing', class: 'ongoing' };
  };

  if (loading) return <div className="loading">Loading events...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="container">
      <div className="page-header">
        <h1>Events</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {canViewAllEvents && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowAllEvents(prev => !prev)}
            >
              {showAllEvents ? 'Show My Events' : 'Show All Events'}
            </button>
          )}
          <button onClick={() => { resetForm(true); setShowModal(true); }} className="btn btn-primary">
            ➕ Create Event
          </button>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="empty-state">
          <p>📅 No events yet. Create your first event to start tracking registrations!</p>
        </div>
      ) : (
        <div className="events-grid">
          {events.map(event => {
            const status = getEventStatus(event);
            return (
              <div key={event.EventId} className="event-card">
                <div className={`event-status-badge ${status.class}`}>{status.label}</div>
                
                <h3>{event.Title}</h3>
                {showAllEvents && canViewAllEvents && (
                  <p className="event-description" style={{ marginBottom: '6px' }}>
                    Owner: {event.OwnerDisplayName || event.OwnerEmail || event.UserId}
                  </p>
                )}
                {event.Description && <p className="event-description">{event.Description}</p>}
                
                <div className="event-details">
                  <div className="event-detail">
                    <span className="icon">📅</span>
                    <span>{formatDate(event.EventDate, event.EventTimeZone)}</span>
                  </div>
                  
                  {event.Location && (
                    <div className="event-detail">
                      <span className="icon">📍</span>
                      <span>{event.Location}</span>
                    </div>
                  )}
                  
                  {event.VirtualLink && (
                    <div className="event-detail">
                      <span className="icon">🔗</span>
                      <a href={event.VirtualLink} target="_blank" rel="noopener noreferrer">Virtual Link</a>
                    </div>
                  )}
                  
                  <div className="event-detail">
                    <span className="icon">👥</span>
                    <span>{event.TotalRegistrations || 0} registered{event.MaxAttendees ? ` / ${event.MaxAttendees} max` : ''}</span>
                  </div>
                  
                  {event.TotalAttended > 0 && (
                    <div className="event-detail">
                      <span className="icon">✅</span>
                      <span>{event.TotalAttended} attended</span>
                    </div>
                  )}
                </div>
                
                <div className="event-actions">
                  <button 
                    onClick={() => navigate(`/events/${event.EventId}`)} 
                    className="btn btn-secondary btn-sm"
                  >
                    View Details
                  </button>
                  <button 
                    onClick={() => handleEdit(event)} 
                    className="btn btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  {!event.TemplateId && (
                    <button 
                      onClick={() => handleGenerateTemplate(event.EventId)} 
                      className="btn btn-secondary btn-sm"
                      title="Generate invitation template"
                    >
                      📧 Template
                    </button>
                  )}
                  <button 
                    onClick={() => openDeleteModal(event)} 
                    className="btn btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        show={showDeleteModal}
        title="Delete Event"
        message={`Are you sure you want to delete ${deleteTarget?.Title ? `"${deleteTarget.Title}"` : 'this event'}? All registrations will be lost.`}
        onConfirm={handleDelete}
        onCancel={closeDeleteModal}
        confirmText="Delete Event"
        confirmStyle="danger"
      >
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={deleteCleanup}
            onChange={(e) => setDeleteCleanup(e.target.checked)}
          />
          Also delete campaigns and the template created for this event
        </label>

        <p className="warning-text">
          This action is permanent. Deleting campaigns will remove send history and analytics for those campaigns.
        </p>
      </ConfirmModal>

      {/* Template Success Modal */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Template Created</h2>
              <button onClick={() => setShowTemplateModal(false)} className="close-btn">✕</button>
            </div>

            <p>
              Template <strong>{createdTemplate?.Name}</strong> was created successfully.
            </p>

            {createdTemplate?.TemplateId && (
              <a
                className="template-link"
                href={`/templates?edit=${createdTemplate.TemplateId}`}
              >
                View and edit this template
              </a>
            )}

            <div className="modal-actions">
              <button type="button" onClick={() => setShowTemplateModal(false)} className="btn btn-primary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEvent ? 'Edit Event' : 'Create Event'}</h2>
              <button onClick={() => setShowModal(false)} className="close-btn">✕</button>
            </div>
            
            <form onSubmit={handleSubmit} className="event-form">
              <div className="form-group">
                <label>Event Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Annual Tech Conference 2026"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Describe your event..."
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>End Date & Time</label>
                  <input
                    type="datetime-local"
                    name="eventEndDate"
                    value={formData.eventEndDate}
                    onChange={handleInputChange}
                    min={formData.eventDate || undefined}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Time Zone</label>
                <select
                  name="eventTimeZone"
                  value={formData.eventTimeZone}
                  onChange={handleInputChange}
                >
                  <option value="Eastern Standard Time">Eastern Time (ET)</option>
                  <option value="Central Standard Time">Central Time (CT)</option>
                  <option value="Mountain Standard Time">Mountain Time (MT)</option>
                  <option value="Pacific Standard Time">Pacific Time (PT)</option>
                  <option value="UTC">UTC</option>
                </select>
                <small className="helper-text">Stored with the event and used for scheduling consistency.</small>
              </div>
              
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., 123 Main St, New York, NY"
                />
              </div>
              
              <div className="form-group">
                <label>Virtual Link</label>
                <input
                  type="url"
                  name="virtualLink"
                  value={formData.virtualLink}
                  onChange={handleInputChange}
                  placeholder="e.g., https://zoom.us/j/123456789"
                />
              </div>

              <div className="form-group">
                <label>Event Contact</label>
                <div className="event-contact-typeahead" ref={eventContactSearchContainerRef}>
                  <input
                    type="text"
                    name="eventContact"
                    value={formData.eventContact}
                    onChange={(e) => handleEventContactChange(e.target.value)}
                    onFocus={() => {
                      if (eventContactSuggestions.length > 0) {
                        setShowEventContactSuggestions(true);
                      }
                    }}
                    autoComplete="off"
                    placeholder="Type to search Entra users (supports comma-separated emails)"
                  />
                  {showEventContactSuggestions && (
                    <div className="event-contact-suggestions" role="listbox">
                      {searchingEventContacts ? (
                        <div className="event-contact-suggestion muted">Searching users...</div>
                      ) : eventContactSuggestions.length === 0 ? (
                        <div className="event-contact-suggestion muted">No Entra users found</div>
                      ) : (
                        eventContactSuggestions.map(userItem => (
                          <button
                            key={userItem.id || userItem.email}
                            type="button"
                            className="event-contact-suggestion"
                            onClick={() => handleSelectEventContactSuggestion(userItem)}
                          >
                            <span className="name">{userItem.displayName || userItem.email}</span>
                            <span className="email">{userItem.email}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <small className="helper-text">Search Entra users; if tenant-wide lookup is restricted, results fall back to your Outlook contacts and known app users. You can add multiple contacts separated by commas.</small>
                {eventContactSearchError && <small className="helper-text" style={{ color: '#a4262c', display: 'block', marginTop: '4px' }}>{eventContactSearchError}</small>}
              </div>
              
              <div className="form-group">
                <label>Maximum Attendees</label>
                <input
                  type="number"
                  name="maxAttendees"
                  value={formData.maxAttendees}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div className="form-group">
                <label>RSVP Required Fields</label>
                <div className="checkbox-list">
                  <label>
                    <input
                      type="checkbox"
                      name="requireFirstName"
                      checked={formData.requireFirstName}
                      onChange={handleInputChange}
                    />
                    <span>First Name</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      name="requireLastName"
                      checked={formData.requireLastName}
                      onChange={handleInputChange}
                    />
                    <span>Last Name</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      name="requireCompany"
                      checked={formData.requireCompany}
                      onChange={handleInputChange}
                    />
                    <span>Company</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      name="requirePhone"
                      checked={formData.requirePhone}
                      onChange={handleInputChange}
                    />
                    <span>Phone</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      name="requireNotes"
                      checked={formData.requireNotes}
                      onChange={handleInputChange}
                    />
                    <span>Notes</span>
                  </label>
                </div>
              </div>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleInputChange}
                  />
                  <span>Public Event</span>
                </label>
              </div>

              {!editingEvent ? (
                <div className="questions-builder">
                  <h3>Registration Questions</h3>
                  <p className="helper-text">Optional questions shown on the RSVP form.</p>

                  {questions.length > 0 && (
                    <div className="questions-list">
                      {questions.map((question, index) => (
                        <div key={`${question.questionText}-${index}`} className="question-item">
                          <div>
                            <strong>{question.questionText}</strong>
                            <div className="question-meta">
                              Type: {question.questionType} {question.isRequired ? '• Required' : ''}
                            </div>
                            {question.options && (
                              <div className="question-options">Options: {question.options}</div>
                            )}
                          </div>
                          <div className="question-actions">
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => removeQuestion(index)}>
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="form-group">
                    <label>Question Text</label>
                    <input
                      type="text"
                      name="questionText"
                      value={questionForm.questionText}
                      onChange={handleQuestionChange}
                      placeholder="e.g., What is your t-shirt size?"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Question Type</label>
                      <select name="questionType" value={questionForm.questionType} onChange={handleQuestionChange}>
                        <option value="text">Text</option>
                        <option value="textarea">Long Text</option>
                        <option value="select">Dropdown</option>
                        <option value="radio">Single Choice</option>
                        <option value="checkbox">Multiple Choice</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Sort Order</label>
                      <input
                        type="number"
                        name="sortOrder"
                        value={questionForm.sortOrder}
                        onChange={handleQuestionChange}
                      />
                    </div>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="isRequired"
                        checked={questionForm.isRequired}
                        onChange={handleQuestionChange}
                      />
                      <span>Required</span>
                    </label>
                  </div>

                  {(questionForm.questionType === 'select' || questionForm.questionType === 'radio' || questionForm.questionType === 'checkbox') && (
                    <div className="form-group">
                      <label>Options (comma separated)</label>
                      <input
                        type="text"
                        name="options"
                        value={questionForm.options}
                        onChange={handleQuestionChange}
                        placeholder="Small, Medium, Large"
                      />
                    </div>
                  )}

                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={addQuestion}>
                      Add Question
                    </button>
                  </div>
                </div>
              ) : (
                <p className="helper-text">
                  To manage registration questions for this event, open the event and use "Manage Questions."
                </p>
              )}
              
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
